import { SolrParamsKey } from '#shared/solr'
import { pageQueryOptions } from '~/utils/literature/queries'
import { getBodyPageNumbers } from '~/utils/literature/virtual-page-segments'

interface ResolvePageIndexByAnchorOptions {
  pageNumber: string
  bookId: string
  queryClient: ReturnType<typeof useQueryClient>
}

export async function resolvePageIndexByAnchorFallback(options: ResolvePageIndexByAnchorOptions): Promise<number | null> {
  const { pageNumber, bookId, queryClient } = options

  const numericPage = Number.parseInt(pageNumber, 10)
  if (!Number.isFinite(numericPage) || numericPage <= 0) {
    return null
  }

  const windowSize = 60
  const start = Math.max(1, numericPage - windowSize)
  const end = numericPage + windowSize

  try {
    const query = pageQueryOptions(
      () => bookId,
      () => ({
        [SolrParamsKey.Pages]: [start, end],
        rows: windowSize * 2 + 1,
      }),
    )

    const res = await queryClient.fetchQuery(query)
    const docs = res?.response?.docs ?? []

    queryClient.removeQueries({ queryKey: query.queryKey })

    const pageWithAnchor = docs.find((doc) => {
      const bodytext = doc?.bodytext
      if (typeof bodytext !== 'string' || bodytext.length === 0) {
        return false
      }

      const pageNumbers = getBodyPageNumbers(bodytext)
      return pageNumbers.includes(String(numericPage))
    })

    const pageCounter = pageWithAnchor?.pageCounter
    if (typeof pageCounter === 'number' && Number.isFinite(pageCounter) && pageCounter > 0) {
      return pageCounter
    }

    return null
  }
  catch {
    return null
  }
}
